import { useEffect, useRef, useState } from "react";
import { usePublicSettings } from "@/hooks/useCMSSettings";

interface TurnstileWidgetProps {
  onVerify: (token: string) => void;
  action?: string;
}

export default function TurnstileWidget({ onVerify, action = "submit" }: TurnstileWidgetProps) {
  const { security } = usePublicSettings();
  const containerRef = useRef<HTMLDivElement>(null);
  const [widgetId, setWidgetId] = useState<string | null>(null);

  useEffect(() => {
    // If not enabled or no site key, we simulate verification immediately
    if (!security?.turnstile?.enabled || !security?.turnstile?.siteKey) {
      onVerify("disabled_or_missing_key");
      return;
    }

    const siteKey = security.turnstile.siteKey;

    // Callback for when turnstile script loads
    window.onloadTurnstileCallback = () => {
      if (containerRef.current && window.turnstile) {
        const id = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action: action,
          callback: function(token: string) {
            onVerify(token);
          },
          theme: "light",
        });
        setWidgetId(id);
      }
    };

    // Load the script if not already loaded
    if (!document.getElementById("turnstile-script")) {
      const script = document.createElement("script");
      script.id = "turnstile-script";
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onloadTurnstileCallback";
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    } else if (window.turnstile && containerRef.current && !widgetId) {
      // Script already loaded, render immediately
      const id = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        action: action,
        callback: function(token: string) {
          onVerify(token);
        },
        theme: "light",
      });
      setWidgetId(id);
    }

    return () => {
      if (widgetId && window.turnstile) {
        window.turnstile.remove(widgetId);
      }
    };
  }, [security, onVerify, action, widgetId]);

  if (!security?.turnstile?.enabled || !security?.turnstile?.siteKey) {
    return null;
  }

  return <div ref={containerRef} className="my-4 flex justify-center" />;
}

// Add turnstile types to window
declare global {
  interface Window {
    turnstile?: {
      render: (element: string | HTMLElement, options: any) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId: string) => void;
      getResponse: (widgetId?: string) => string | undefined;
    };
    onloadTurnstileCallback?: () => void;
  }
}
